import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import MessageBubble from "../components/chat/MessageBubble";
import NewDmModal from "../components/chat/NewDmModal";
import LoadingSpinner from "../components/LoadingSpinner";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { fmtAgo } from "../lib/format";
import {
  type ChatMessage,
  type ChatThread,
  fetchChatMessages,
  fetchChatThreads,
  markChatThreadRead,
  postChatMessage,
} from "../services/chatApi";

const THREAD_POLL_MS = 15_000; // thread-list refresh
const MESSAGE_POLL_MS = 5_000; // open-thread tail-poll

/**
 * Messages page — 1-on-1 DMs.
 *   Left: thread list (other user + last preview + unread badge).
 *   Right: messages of the selected thread + composer.
 *   "New DM" button (platform users only) opens a target picker.
 *
 * Customer/vendor users see their auto-routed Jason thread on first load;
 * they can't start new DMs (the New-DM button is hidden for them).
 */
export const MessagesPage: React.FC = () => {
  const { token, user, role } = useAuth();
  const [threads, setThreads] = useState<ChatThread[] | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showNewDm, setShowNewDm] = useState(false);
  const messagePaneRef = useRef<HTMLDivElement | null>(null);
  // Highest message id we currently hold — read by the tail-poll to compute
  // `since` without reaching into a setMessages updater. Kept in sync below.
  const lastMsgIdRef = useRef(0);
  // True while a tail-poll fetch is outstanding, so a tick that takes longer
  // than the 5s cadence can't fire a second overlapping poll (which would
  // re-fetch the same `since` and double-append duplicate bubbles).
  const pollInFlightRef = useRef(false);

  const refreshThreads = useCallback(async () => {
    if (!token) return;
    try {
      const d = await fetchChatThreads(token);
      setThreads(d.threads);
      // Auto-select the first thread if nothing is open yet (mostly hits
      // the auto-created Jason thread for customer/vendor users).
      setActiveId((prev) => prev ?? (d.threads[0]?.thread_id ?? null));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [token]);

  const refreshMessages = useCallback(
    async (tid: number, sinceId?: number) => {
      if (!token) return;
      try {
        const d = await fetchChatMessages(tid, token, sinceId ? { sinceId } : {});
        if (sinceId === undefined) {
          setMessages(d.messages);
        } else if (d.messages.length > 0) {
          // Tail-append; assumes the response is oldest -> newest within the page.
          // Dedup against the current max id so an overlapping/duplicate poll
          // can't append messages we already hold (duplicate bubbles + keys).
          setMessages((prev) => {
            const maxId = prev.reduce((mx, m) => Math.max(mx, m.message_id), 0);
            const fresh = d.messages.filter((m) => m.message_id > maxId);
            return fresh.length > 0 ? [...prev, ...fresh] : prev;
          });
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [token],
  );

  // Initial + polled thread-list refresh.
  useEffect(() => {
    void refreshThreads();
    const id = window.setInterval(() => void refreshThreads(), THREAD_POLL_MS);
    return () => window.clearInterval(id);
  }, [refreshThreads]);

  // Open-thread initial load + mark-read + tail-poll for new messages.
  useEffect(() => {
    if (activeId == null || !token) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    setMessages([]);
    // Fresh thread: no poll should be considered in-flight from a prior one.
    pollInFlightRef.current = false;
    void refreshMessages(activeId).then(() => {
      if (!cancelled && token) {
        // Marking read clears the unread badge on this thread; refresh the
        // list so the count updates without a roundtrip-delay.
        void markChatThreadRead(activeId, token).then(() => refreshThreads());
      }
    });
    const id = window.setInterval(() => {
      // Skip this tick if the previous poll hasn't returned yet, so a
      // slow (>5s) fetch can't overlap and double-append.
      if (pollInFlightRef.current) return;
      // Tail-poll only messages newer than the highest id we hold (from a ref,
      // not a setMessages updater).
      pollInFlightRef.current = true;
      void refreshMessages(activeId, lastMsgIdRef.current).finally(() => {
        pollInFlightRef.current = false;
      });
    }, MESSAGE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [activeId, token, refreshMessages, refreshThreads]);

  // Keep the max-message-id ref in sync + auto-scroll to bottom on new messages.
  useEffect(() => {
    lastMsgIdRef.current = messages.reduce((mx, m) => Math.max(mx, m.message_id), 0);
    const el = messagePaneRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const activeThread = useMemo(
    () => threads?.find((t) => t.thread_id === activeId) ?? null,
    [threads, activeId],
  );

  const sendMessage = async (): Promise<void> => {
    if (activeId == null || !token) return;
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      const r = await postChatMessage(activeId, body, token);
      // Optimistic append (the tail-poll would also pick it up).
      setMessages((prev) => [...prev, r.message]);
      setDraft("");
      // Reflect the new last-message preview in the sidebar list.
      void refreshThreads();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  };

  // Platform users can start DMs with other platform users. Customers/vendors
  // can't (their target list is just Jason, who's auto-routed already).
  const canStartDm = role === "platform";

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col md:flex-row p-4 md:p-6 gap-4">
      <aside className="md:w-72 shrink-0 flex flex-col bg-white border border-secondary-200 rounded-md overflow-hidden">
        <div className="px-3 py-2 border-b border-secondary-200 flex items-center justify-between">
          <div className="font-semibold text-sm">Messages</div>
          {canStartDm && (
            <Button type="button" size="sm" variant="outline" onClick={() => setShowNewDm(true)}>
              + New DM
            </Button>
          )}
        </div>
        {threads === null ? (
          <div className="p-4">
            <LoadingSpinner label="loading threads" />
          </div>
        ) : threads.length === 0 ? (
          <div className="p-4 text-sm text-secondary-500">
            No conversations yet.
            {canStartDm && " Click + New DM to start one."}
          </div>
        ) : (
          <ul className="flex-1 overflow-y-auto divide-y divide-secondary-100">
            {threads.map((t) => (
              <li key={t.thread_id}>
                <button
                  type="button"
                  onClick={() => setActiveId(t.thread_id)}
                  className={
                    "w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-secondary-50 " +
                    (activeId === t.thread_id ? "bg-primary-50" : "")
                  }
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm truncate">
                        {t.other_user.name || t.other_user.email || `User ${t.other_user.user_id}`}
                      </span>
                      {t.unread_count > 0 && (
                        <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-600 text-white">
                          {t.unread_count}
                        </span>
                      )}
                    </div>
                    {t.other_user.company_name && (
                      <div className="text-[11px] text-secondary-500 truncate">
                        {t.other_user.company_name}
                        {t.other_user.role ? ` · ${t.other_user.role}` : ""}
                      </div>
                    )}
                    {t.last_message_preview && (
                      <div className="text-xs text-secondary-600 truncate mt-0.5">
                        {t.last_message_preview}
                      </div>
                    )}
                    {t.last_message_utc && (
                      <div className="text-[10px] text-secondary-400 mt-0.5">
                        {fmtAgo(t.last_message_utc)}
                      </div>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className="flex-1 flex flex-col bg-white border border-secondary-200 rounded-md overflow-hidden min-h-0">
        {activeThread === null ? (
          <div className="flex-1 flex items-center justify-center text-sm text-secondary-500 p-6">
            Select a conversation to view messages.
          </div>
        ) : (
          <>
            <div className="px-4 py-2 border-b border-secondary-200">
              <div className="font-semibold text-sm">
                {activeThread.other_user.name || activeThread.other_user.email}
              </div>
              {activeThread.other_user.email && (
                <div className="text-xs text-secondary-500">
                  {activeThread.other_user.email}
                  {activeThread.other_user.company_name
                    ? ` · ${activeThread.other_user.company_name}`
                    : ""}
                  {activeThread.other_user.role
                    ? ` · ${activeThread.other_user.role}`
                    : ""}
                </div>
              )}
            </div>
            <div
              ref={messagePaneRef}
              className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0"
            >
              {messages.length === 0 ? (
                <div className="text-sm text-secondary-500 text-center py-6">
                  No messages yet. Say hello.
                </div>
              ) : (
                messages.map((m) => (
                  <MessageBubble
                    key={m.message_id}
                    message={m}
                    mine={user?.user_id === m.user_id}
                    onMutated={() => {
                      // Edits and reactions both change the per-message
                      // shape — refetch the full message list so counts
                      // and `(edited)` markers stay in sync.
                      if (activeThread) void refreshMessages(activeThread.thread_id);
                    }}
                  />
                ))
              )}
            </div>
            {error && (
              <div className="mx-3 mb-2 text-xs text-error-700 bg-error-50 px-2 py-1 rounded">
                {error}
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void sendMessage();
              }}
              className="border-t border-secondary-200 p-2 flex gap-2"
            >
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onComposerKeyDown}
                rows={2}
                placeholder="Type a message — Enter to send, Shift+Enter for newline"
                className="flex-1 text-sm border border-secondary-300 rounded px-2 py-1.5"
                disabled={busy}
              />
              <Button type="submit" size="sm" disabled={busy || !draft.trim()}>
                {busy ? "…" : "Send"}
              </Button>
            </form>
          </>
        )}
      </section>

      <NewDmModal
        open={showNewDm}
        onClose={() => setShowNewDm(false)}
        onPicked={(tid) => {
          setShowNewDm(false);
          setActiveId(tid);
          void refreshThreads();
        }}
      />
    </div>
  );
};


export default MessagesPage;
